import type { DraftTask, TaskSize } from '@dyspatch/core';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/components/auth/session';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Segmented } from '@/components/ui/segmented';
import { useProjects, useSchedulePresets } from '@/hooks/use-fleet';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import {
  CADENCE_OPTIONS,
  type Cadence,
  type CadenceKind,
  cadenceProblem,
  cronOf,
  DEFAULT_CADENCE,
  describeCadence,
  WEEKDAY_LABELS,
} from '@/lib/cadence';
import { cn } from '@/lib/utils';

/**
 * Making a schedule: three questions, in the order a person answers them
 * (docs/JOB-LIFECYCLE.md 2.7).
 *
 * The difference from opening a job is where the plan comes from. A job is a
 * goal a worker plans and a person then approves; a schedule cannot wait for a
 * worker every night at 02:00, so the tasks are written here and pressing the
 * button is the approval, for every run the schedule will ever make. The copy
 * says so at the top and again on the button, because approving something once
 * for an unattended repeat is the one consequential thing on this form.
 *
 * What this form used to ask for and no longer does, because the answer was
 * already on the page:
 *
 * - **A repo per task.** A schedule names its repo once. A task inherits it, and
 *   the field only appears when the schedule names more than one, which is the
 *   only case where a task's repo is a real question. `AmendComposer` has always
 *   worked this way; this form was the outlier, and the outcome was the refusal
 *   "T1 needs a repo" on a form that had just been told the repo.
 * - **A key per task.** `T1`, `T2` are graph-local names, assigned by position.
 *   A person typing them can collide, skip, or rename one and orphan an edge.
 *   They are derived here and shown, not typed.
 * - **A `dependsOn` string.** Typing "after T1" into a text field is how a
 *   dangling edge gets made. A task now picks the earlier tasks it waits for, so
 *   a forward edge and a cycle are unrepresentable rather than refused.
 * - **A separate job title.** Every schedule was named twice. The job each run
 *   files takes the schedule's name unless someone says otherwise, under More.
 *
 * Vocabulary note, as in `BriefComposer`: the console says "job" and "task"; the
 * hub, the API and this file's `api.createSchedule` still say brief, and the
 * local `StepDraft`/`emptyStep` names are wire-adjacent and stay put (issue
 * #60's rule, the same one `briefId` follows).
 */

const SIZES: readonly { value: TaskSize; label: string }[] = [
  { value: 'xs', label: 'xs' },
  { value: 's', label: 's' },
  { value: 'm', label: 'm' },
  { value: 'l', label: 'l' },
];

/** The hub's cap on a template's plan, mirrored so the form can say so first. */
const MAX_STEPS = 40;

interface StepDraft {
  /**
   * Stable across edits and reorders. It is what `dependsOn` points at, which is
   * why an edge survives a step being renamed, moved or renumbered; and it is
   * React's key, so typing in a row never remounts it and drops the caret.
   */
  id: string;
  title: string;
  spec: string;
  criteria: string;
  /** Empty means "the schedule's repo", which is what almost every step means. */
  repo: string;
  size: TaskSize;
  /** Ids of earlier steps, never keys: see the note on `id`. */
  dependsOn: string[];
}

let nextStepId = 0;

function emptyStep(): StepDraft {
  return {
    id: `step-${(nextStepId += 1)}`,
    title: '',
    spec: '',
    criteria: '',
    repo: '',
    size: 's',
    dependsOn: [],
  };
}

/** The graph-local name of the step in position `index`. Derived, never typed. */
function keyOf(index: number): string {
  return `T${index + 1}`;
}

function lines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * Edges only ever point backwards, so a reorder or a deletion can leave one
 * pointing at a step that is now later or gone. Pruning here rather than
 * refusing at submit is the difference between a form that fixes itself and one
 * that blames you for using its own arrows.
 */
function prune(steps: readonly StepDraft[]): StepDraft[] {
  return steps.map((step, index) => {
    const earlier = new Set(steps.slice(0, index).map((other) => other.id));
    const kept = step.dependsOn.filter((id) => earlier.has(id));
    return kept.length === step.dependsOn.length ? step : { ...step, dependsOn: kept };
  });
}

function toDraftTask(step: StepDraft, index: number, steps: readonly StepDraft[], repo: string) {
  const at = new Map(steps.map((other, position) => [other.id, position]));
  return {
    key: keyOf(index),
    title: step.title.trim(),
    spec: step.spec.trim(),
    acceptanceCriteria: lines(step.criteria),
    repo: step.repo.trim() || repo,
    size: step.size,
    priority: 'normal',
    dependsOn: step.dependsOn
      .map((id) => at.get(id))
      .filter((position): position is number => position !== undefined)
      .map(keyOf),
  } satisfies DraftTask;
}

/**
 * A preset's plan, in this form's terms. Keys arrive from the hub and leave
 * again derived from position, so an edge is carried across by resolving the
 * preset's key to the step it named.
 */
function fromDraftTasks(tasks: readonly DraftTask[]): StepDraft[] {
  const drafts = tasks.map((task) => ({ ...emptyStep(), task }));
  const idByKey = new Map(drafts.map(({ id }, index) => [tasks[index]!.key, id]));
  return prune(
    drafts.map(({ id, task }) => ({
      id,
      title: task.title,
      spec: task.spec,
      criteria: task.acceptanceCriteria.join('\n'),
      repo: task.repo,
      size: task.size,
      dependsOn: task.dependsOn
        .map((key) => idByKey.get(key))
        .filter((value): value is string => value !== undefined),
    })),
  );
}

/**
 * Why the hub would refuse this step, in the reader's terms rather than as a zod
 * path in a toast. Returned per step so the sentence can sit under the field it
 * is about, rather than all of them queueing at the bottom one at a time.
 */
function stepProblem(step: StepDraft): string | null {
  if (step.title.trim().length < 3) return 'Needs a title.';
  if (step.spec.trim().length < 10) return 'Needs a spec the agent can act on.';
  if (lines(step.criteria).length === 0) {
    return 'Needs at least one acceptance criterion: what makes it done.';
  }
  return null;
}

/**
 * Everything above the steps, plus the first step that is not finished. Only the
 * rules that are cheap and certain here; the graph's own rules are the hub's
 * answer, and it writes them as sentences too.
 */
function formProblem(input: {
  name: string;
  repos: readonly string[];
  goal: string;
  steps: readonly StepDraft[];
}): string | null {
  if (input.name.trim().length < 3) return 'A schedule needs a name.';
  if (!input.repos.length) return 'A schedule needs a repo to run against.';
  if (input.goal.trim().length < 10) return 'Say what each run is for, in a sentence.';
  if (!input.steps.length)
    return 'A schedule needs at least one task; nothing would run otherwise.';
  if (input.steps.length > MAX_STEPS) {
    return `That is ${input.steps.length} tasks; the hub takes ${MAX_STEPS}.`;
  }
  for (const [index, step] of input.steps.entries()) {
    const problem = stepProblem(step);
    if (problem) return `${keyOf(index)}: ${problem.slice(0, 1).toLowerCase()}${problem.slice(1)}`;
  }
  return null;
}

/** A numbered block, so the form reads as three questions rather than nine fields. */
function Block({
  step,
  title,
  hint,
  children,
}: {
  step: number;
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <span className="bg-raised text-fg-2 grid size-5 shrink-0 place-items-center rounded-full text-2xs font-medium tabular-nums">
          {step}
        </span>
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-fg-3 text-2xs">{hint}</p>
      </div>
      <div className="space-y-2.5 pl-7">{children}</div>
    </section>
  );
}

export function ScheduleComposer({ onClose }: { onClose: () => void }) {
  const { session } = useSession();
  const projects = useProjects();
  const presets = useSchedulePresets();
  const known = useMemo(
    () => (projects.data ?? []).map((project) => project.repo),
    [projects.data],
  );

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [goal, setGoal] = useState('');
  const [repos, setRepos] = useState<string[]>([]);
  const [constraints, setConstraints] = useState('');
  const [cadence, setCadence] = useState<Cadence>(DEFAULT_CADENCE);
  const [steps, setSteps] = useState<StepDraft[]>([emptyStep()]);
  const [moreOpen, setMoreOpen] = useState(false);
  // A form that greys its own button out and says nothing is a form that refuses
  // to say what it wants. The button stays live; pressing it either files the
  // schedule or names the first thing missing, and only then does the red text
  // appear beside the fields it is about.
  const [tried, setTried] = useState(false);
  const first = useRef<HTMLInputElement>(null);

  useEffect(() => {
    first.current?.focus();
  }, []);

  // One known repo is not a choice, it is the answer. Prefilled rather than
  // asked, and still editable, because the fleet grows by someone typing a repo
  // the list has never seen.
  useEffect(() => {
    if (known.length === 1 && !repos.length) setRepos([known[0]!]);
  }, [known, repos.length]);

  const repo = repos[0] ?? '';
  const problem = formProblem({ name, repos, goal, steps }) ?? cadenceProblem(cadence);
  // The job each run files is named after the schedule unless someone says
  // otherwise. Two name fields on one form was two chances to disagree with
  // yourself, on a record where the disagreement shows up nightly.
  const jobTitle = title.trim() || name.trim();
  const constraintLines = lines(constraints);
  const hasMore = Boolean(title.trim()) || constraintLines.length > 0;
  const showMore = moreOpen || hasMore;
  // The hub's own zone, which is what a schedule fires in. Shown rather than
  // chosen: one zone for the fleet is the point (2.7), and a per-schedule zone
  // picker would invite two answers to "what time is it". The browser's zone is
  // the fallback only until the hub has answered, and the two are often
  // different, which is exactly why the sentence names the zone at all.
  const timezone = presets.data?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const create = useHubAction(
    () =>
      api.createSchedule({
        name: name.trim(),
        cron: cronOf(cadence),
        template: {
          title: jobTitle,
          goal: goal.trim(),
          constraints: constraintLines,
          repos,
          tasks: steps.map((step, index) => toDraftTask(step, index, steps, repo)),
        },
      }),
    {
      onDone: onClose,
      success: 'Schedule approved. Every run it files carries your approval.',
      inlineError: true,
    },
  );

  function patchStep(index: number, patch: Partial<StepDraft>): void {
    setSteps((current) => current.map((step, at) => (at === index ? { ...step, ...patch } : step)));
  }

  function moveStep(index: number, by: -1 | 1): void {
    setSteps((current) => {
      const to = index + by;
      if (to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(to, 0, moved!);
      return prune(next);
    });
  }

  function applyPreset(key: string): void {
    const preset = (presets.data?.presets ?? []).find((option) => option.key === key);
    if (!preset) return;
    setName(preset.name);
    setTitle(preset.template.title === preset.name ? '' : preset.template.title);
    setGoal(preset.template.goal);
    setRepos([...preset.template.repos]);
    setConstraints(preset.template.constraints.join('\n'));
    setSteps(fromDraftTasks(preset.template.tasks));
    setCadence({ ...DEFAULT_CADENCE, kind: 'cron', expression: preset.cron });
    setTried(false);
  }

  return (
    <section className="bg-card overflow-hidden rounded-lg border">
      <div className="bg-sunken border-b px-gutter py-[calc(var(--dy-row-py)+1px)]">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium">New schedule</h2>
          <span className="text-fg-3 text-2xs">approved as {session.actor}</span>
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-auto"
            onClick={onClose}
            aria-label="Discard this schedule"
          >
            <X />
          </Button>
        </div>
        <p className="text-fg-2 mt-0.5 text-xs">
          A job with a clock on it. You write the steps once and approving them here approves every
          run the schedule ever makes; each run still comes back as a pull request for you to
          review.
        </p>
      </div>

      <form
        className="space-y-4 px-gutter py-3"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
        onSubmit={(event) => {
          event.preventDefault();
          if (problem) {
            setTried(true);
            return;
          }
          create.run();
        }}
      >
        {presets.data?.presets.length ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-fg-3 text-2xs">Start from</span>
            {presets.data.presets.map((preset) => (
              <Button
                key={preset.key}
                type="button"
                size="xs"
                variant="subtle"
                onClick={() => applyPreset(preset.key)}
              >
                {preset.name}
              </Button>
            ))}
            <span className="text-fg-3 text-2xs">or fill it in below.</span>
          </div>
        ) : null}

        <Block step={1} title="What it does" hint="the job every run files">
          <div className="grid gap-2.5 sm:grid-cols-[minmax(0,1fr)_14rem]">
            <div className="space-y-1">
              <Label htmlFor="sc-name">Name</Label>
              <Input
                ref={first}
                id="sc-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={200}
                placeholder="Nightly QA on compliance-app"
                className="h-8 text-base"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sc-repos">Repo</Label>
              <Combobox
                id="sc-repos"
                values={repos}
                onChange={setRepos}
                options={known}
                placeholder="nymbl-app"
              />
              <p className="text-fg-3 text-2xs">
                {repos.length > 1
                  ? 'Every task picks one of these.'
                  : 'Every task runs here unless you add a second repo.'}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="sc-goal">What each run is for</Label>
            <Textarea
              id="sc-goal"
              value={goal}
              onChange={(event) => setGoal(event.target.value)}
              rows={2}
              placeholder="The Playwright suite runs green every weekday morning, and anything that looks like a product bug is written up rather than papered over."
            />
          </div>
        </Block>

        <Block step={2} title="When it runs" hint="in the hub's timezone">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              value={cadence.kind}
              options={CADENCE_OPTIONS}
              onChange={(kind: CadenceKind) => setCadence({ ...cadence, kind })}
              label="How often this schedule runs"
            />
            {cadence.kind === 'cron' ? (
              <Input
                id="sc-cadence"
                value={cadence.expression}
                onChange={(event) => setCadence({ ...cadence, expression: event.target.value })}
                placeholder="0 2 * * 1-5"
                className="max-w-[12rem] font-mono"
                aria-label="Cron expression"
              />
            ) : (
              <>
                {cadence.kind === 'weekly' ? (
                  <select
                    className="dy-inset h-8 px-2 text-base"
                    value={cadence.weekday}
                    onChange={(event) =>
                      setCadence({ ...cadence, weekday: Number(event.target.value) })
                    }
                    aria-label="Which day"
                  >
                    {WEEKDAY_LABELS.map((label, index) => (
                      <option key={label} value={index}>
                        {label}
                      </option>
                    ))}
                  </select>
                ) : null}
                <Input
                  id="sc-cadence"
                  type="time"
                  value={cadence.time}
                  onChange={(event) => setCadence({ ...cadence, time: event.target.value })}
                  className="max-w-[8rem]"
                  aria-label="At what time"
                />
              </>
            )}
          </div>
          <p className="text-fg-3 text-2xs">
            Runs {describeCadence(cadence, timezone)}. The hub fires it in its own zone, which is
            the same one the metrics count office hours in.
          </p>
        </Block>

        <Block
          step={3}
          title="Tasks"
          hint={`${steps.length} · the plan you are approving for every run`}
        >
          {steps.map((step, index) => {
            const trouble = tried ? stepProblem(step) : null;
            const earlier = steps.slice(0, index);
            return (
              <div
                key={step.id}
                className={cn(
                  'dy-inset space-y-2 px-gutter py-2.5',
                  trouble && 'ring-bad/40 ring-1',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-fg-3 w-7 shrink-0 font-mono text-xs">{keyOf(index)}</span>
                  <Input
                    value={step.title}
                    onChange={(event) => patchStep(index, { title: event.target.value })}
                    placeholder="Run the Playwright suite"
                    className="min-w-[12rem] flex-1"
                    aria-label={`Title for task ${keyOf(index)}`}
                  />
                  <Segmented
                    value={step.size}
                    options={SIZES}
                    onChange={(size: TaskSize) => patchStep(index, { size })}
                    label={`Size of task ${keyOf(index)}`}
                  />
                  {/* A single step has nowhere to go and nothing to be removed
                      from; three dead controls on the commonest form is three
                      things to read before finding the one that works. */}
                  <div className={cn('flex items-center', steps.length === 1 && 'hidden')}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      disabled={index === 0}
                      onClick={() => moveStep(index, -1)}
                      aria-label={`Move ${keyOf(index)} earlier`}
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      disabled={index === steps.length - 1}
                      onClick={() => moveStep(index, 1)}
                      aria-label={`Move ${keyOf(index)} later`}
                    >
                      <ArrowDown />
                    </Button>
                    {steps.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        onClick={() =>
                          setSteps((current) => prune(current.filter((_, at) => at !== index)))
                        }
                        aria-label={`Remove task ${keyOf(index)}`}
                      >
                        <X />
                      </Button>
                    ) : null}
                  </div>
                </div>

                <Textarea
                  value={step.spec}
                  onChange={(event) => patchStep(index, { spec: event.target.value })}
                  rows={3}
                  placeholder="What the agent should do, in enough detail that it needs nothing else."
                  className="font-mono text-xs"
                  aria-label={`Spec for task ${keyOf(index)}`}
                />

                <div
                  className={cn(
                    'grid gap-2',
                    repos.length > 1
                      ? 'sm:grid-cols-[minmax(0,1fr)_10rem]'
                      : 'sm:grid-cols-[minmax(0,1fr)]',
                  )}
                >
                  <Textarea
                    value={step.criteria}
                    onChange={(event) => patchStep(index, { criteria: event.target.value })}
                    rows={2}
                    placeholder={'What makes it done, one per line'}
                    aria-label={`Acceptance criteria for task ${keyOf(index)}`}
                  />
                  {/* Asked only when it is a real question. One repo on the
                      schedule means one repo on the step, and a blank field that
                      refuses the form is not a question, it is a trap. */}
                  {repos.length > 1 ? (
                    <select
                      className="dy-inset h-8 self-start px-2 text-sm"
                      value={step.repo || repo}
                      onChange={(event) => patchStep(index, { repo: event.target.value })}
                      aria-label={`Repo for task ${keyOf(index)}`}
                    >
                      {repos.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>

                {earlier.length ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-fg-3 text-2xs">Waits for</span>
                    {earlier.map((other, at) => {
                      const on = step.dependsOn.includes(other.id);
                      return (
                        <button
                          key={other.id}
                          type="button"
                          aria-pressed={on}
                          onClick={() =>
                            patchStep(index, {
                              dependsOn: on
                                ? step.dependsOn.filter((id) => id !== other.id)
                                : [...step.dependsOn, other.id],
                            })
                          }
                          className={cn(
                            'border-line-2 rounded-full border px-2 py-0.5 font-mono text-2xs transition-colors duration-100',
                            on ? 'bg-raised text-foreground' : 'text-fg-3 hover:text-foreground',
                          )}
                        >
                          {keyOf(at)}
                        </button>
                      );
                    })}
                    {step.dependsOn.length ? null : (
                      <span className="text-fg-3 text-2xs">
                        · none picked, so it starts with the run
                      </span>
                    )}
                  </div>
                ) : null}

                {trouble ? (
                  <p className="text-bad text-2xs" role="alert">
                    {trouble}
                  </p>
                ) : null}
              </div>
            );
          })}

          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={steps.length >= MAX_STEPS}
            onClick={() => setSteps([...steps, emptyStep()])}
          >
            <Plus aria-hidden />
            Add a task
          </Button>
        </Block>

        <div className="space-y-2.5">
          <button
            type="button"
            className="text-fg-2 hover:text-fg flex items-center gap-1 text-xs"
            onClick={() => setMoreOpen((current) => !current)}
            aria-expanded={showMore}
            aria-controls="sc-more"
          >
            {showMore ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            More options
            <span className="text-fg-3">rules to follow, a different job title</span>
          </button>

          {showMore ? (
            <div id="sc-more" className="grid gap-2.5 pl-5 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="sc-title">Each run files a job called</Label>
                <Input
                  id="sc-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  maxLength={200}
                  placeholder={name.trim() || 'Nightly QA'}
                  className="h-8 text-base"
                />
                <p className="text-fg-3 text-2xs">Empty means the schedule's own name.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sc-constraints">Rules to follow</Label>
                <Textarea
                  id="sc-constraints"
                  value={constraints}
                  onChange={(event) => setConstraints(event.target.value)}
                  rows={2}
                  placeholder={'No new dependencies'}
                />
                <p className="text-fg-3 text-2xs">One per line. Optional.</p>
              </div>
            </div>
          ) : null}
        </div>

        {tried && problem ? (
          <p className="text-bad text-xs" role="alert">
            {problem}
          </p>
        ) : null}
        {create.error ? (
          <p className="text-bad text-xs" role="alert">
            {create.error}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-1.5 border-t pt-3">
          <Button
            type="submit"
            size="sm"
            variant="gate"
            disabled={create.busy}
            className="max-sm:h-9"
          >
            {create.busy ? 'Approving…' : 'Approve and schedule'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose} className="max-sm:h-9">
            Cancel
          </Button>
          <span className="text-fg-3 ml-auto hidden text-2xs sm:block">
            {problem
              ? 'esc to close'
              : `${steps.length} task${steps.length === 1 ? '' : 's'} on ${repo}, ${describeCadence(cadence, timezone)} · esc to close`}
          </span>
        </div>
      </form>
    </section>
  );
}
